import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Prescription } from '../../types';
import { Pill, CheckCircle, XCircle, Printer, Check, X, FileText } from 'lucide-react';
export const PharmacyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  // Use ID pattern for live updates
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Local state to track dispensing before saving
  const [dispenseStatus, setDispenseStatus] = useState<Record<string, 'Dispensed' | 'Not Dispensed'>>({});
  // State for showing the prescription preview modal
  const [showPrescriptionPreview, setShowPrescriptionPreview] = useState(false);
  const pendingEncounters = encounters.filter(e => e.stage === 'PHARMACY');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    const initialStatus: Record<string, 'Dispensed' | 'Not Dispensed'> = {};
    encounter.prescriptions.forEach(rx => {
      if (rx.status !== 'Pending') {
        initialStatus[rx.id] = rx.status;
      }
    });
    setDispenseStatus(initialStatus);
    setShowPrescriptionPreview(false);
  };
  const handleToggleStatus = (rxId: string, status: 'Dispensed' | 'Not Dispensed') => {
    setDispenseStatus(prev => ({
      ...prev,
      [rxId]: status
    }));
  };
  const handleCompleteDispensing = () => {
    if (!selectedEncounter || !currentUser) return;
    // Check if all drugs have a status
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    if (!allProcessed) {
      alert('Please mark all drugs as Dispensed or Not Available.');
      return;
    }
    const updatedPrescriptions = selectedEncounter.prescriptions.map(rx => ({
      ...rx,
      status: dispenseStatus[rx.id],
      dispensedAt: new Date().toISOString(),
      dispensedBy: currentUser.id,
      pharmacistName: currentUser.fullName
    }));
    const hasUndispensed = updatedPrescriptions.some(rx => rx.status === 'Not Dispensed');
    updateEncounter(selectedEncounter.id, {
      prescriptions: updatedPrescriptions,
      stage: 'CLINICIAN'
    });
    alert(hasUndispensed ? 'Dispensing completed. Report sent back to Clinician for review (some drugs were not available).' : 'Dispensing completed. Report sent back to Clinician.');
    setSelectedEncounterId(null);
    setDispenseStatus({});
    setShowPrescriptionPreview(false);
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-3986">Patient not found</div>;
    const notDispensedDrugs = selectedEncounter.prescriptions.filter(rx => dispenseStatus[rx.id] === 'Not Dispensed');
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    // If showing the prescription preview
    if (showPrescriptionPreview) {
      return <div className="space-y-6 pb-20" data-id="element-3987">
          <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-3988">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3989">
              Prescription Preview
            </h1>
            <div className="flex space-x-2" data-id="element-3990">
              <Button variant="outline" onClick={() => setShowPrescriptionPreview(false)} data-id="element-3991">
                Back to Dispensing
              </Button>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700" data-id="element-3992">
                <Printer className="w-4 h-4 mr-2" data-id="element-3993" /> Print Prescription
              </Button>
            </div>
          </div>

          {/* A4 Prescription Form Container */}
          <div className="flex justify-center print:block print:m-0" data-id="element-3994">
            <div className="bg-white shadow-lg print:shadow-none w-full max-w-[210mm] min-h-[297mm] p-10 print:p-0 print:w-full print:max-w-none border border-gray-200 print:border-none" data-id="element-3995">
              {/* Form Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-3996">
                <div className="flex items-center" data-id="element-3997">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-3998" />
                  <div data-id="element-3999">
                    <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-4000">
                      BMED CARE
                    </h1>
                    <p className="text-sm italic text-gray-600" data-id="element-4001">
                      Maternity Care Made Better
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-4002">
                  <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-4003">
                    PRESCRIPTION FORM
                  </p>
                  <p className="text-sm" data-id="element-4004">
                    <span className="font-semibold" data-id="element-4005">Date:</span>{' '}
                    {new Date().toLocaleDateString()}
                  </p>
                  <p className="text-sm" data-id="element-4006">
                    <span className="font-semibold" data-id="element-4007">File No:</span>{' '}
                    {patient.bmcFileNumber}
                  </p>
                </div>
              </div>

              {/* Patient Details */}
              <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-4008">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-4009">
                  <div className="col-span-8" data-id="element-4010">
                    <span className="font-semibold" data-id="element-4011">Patient Name:</span>{' '}
                    {patient.firstName} {patient.lastName}
                  </div>
                  <div className="col-span-2" data-id="element-4012">
                    <span className="font-semibold" data-id="element-4013">Age:</span> {patient.age}
                  </div>
                  <div className="col-span-2" data-id="element-4014">
                    <span className="font-semibold" data-id="element-4015">Sex:</span> {patient.sex}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-4016">
                  <div className="col-span-12" data-id="element-4017">
                    <span className="font-semibold" data-id="element-4018">Allergies:</span>{' '}
                    <span className="text-red-600 font-bold" data-id="element-4019">
                      {patient.allergies}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2" data-id="element-4020">
                  <div className="col-span-12" data-id="element-4021">
                    <span className="font-semibold" data-id="element-4022">
                      Prescribing Clinician:
                    </span>{' '}
                    {selectedEncounter.lastAttendingClinician}
                  </div>
                </div>
              </div>

              {/* Drugs Table */}
              <div className="mb-12" data-id="element-4023">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-4024">
                  Medication to be Sourced Externally
                </h3>
                <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-4025">
                  <thead data-id="element-4026">
                    <tr className="bg-gray-100" data-id="element-4027">
                      <th className="border border-black p-2 w-12" data-id="element-4028">No.</th>
                      <th className="border border-black p-2 text-left" data-id="element-4029">
                        Drug Name
                      </th>
                      <th className="border border-black p-2" data-id="element-4030">Dose</th>
                      <th className="border border-black p-2" data-id="element-4031">Route</th>
                      <th className="border border-black p-2" data-id="element-4032">Frequency</th>
                      <th className="border border-black p-2" data-id="element-4033">Days</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-4034">
                    {notDispensedDrugs.map((rx, i) => <tr key={rx.id} data-id="element-4035">
                        <td className="border border-black p-3 font-medium" data-id="element-4036">
                          {i + 1}
                        </td>
                        <td className="border border-black p-3 text-left font-bold" data-id="element-4037">
                          {rx.drugName}
                        </td>
                        <td className="border border-black p-3" data-id="element-4038">{rx.dose}</td>
                        <td className="border border-black p-3" data-id="element-4039">{rx.route}</td>
                        <td className="border border-black p-3" data-id="element-4040">
                          {rx.frequency}
                        </td>
                        <td className="border border-black p-3" data-id="element-4041">{rx.days}</td>
                      </tr>)}
                    {/* Empty rows for padding if few drugs */}
                    {Array.from({
                    length: Math.max(0, 5 - notDispensedDrugs.length)
                  }).map((_, i) => <tr key={`empty-${i}`} data-id="element-4042">
                        <td className="border border-black p-4" data-id="element-4043"></td>
                        <td className="border border-black p-4" data-id="element-4044"></td>
                        <td className="border border-black p-4" data-id="element-4045"></td>
                        <td className="border border-black p-4" data-id="element-4046"></td>
                        <td className="border border-black p-4" data-id="element-4047"></td>
                        <td className="border border-black p-4" data-id="element-4048"></td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Footer Signatures */}
              <div className="mt-auto pt-10" data-id="element-4049">
                <div className="flex justify-between items-end" data-id="element-4050">
                  <div className="w-64" data-id="element-4051">
                    <p className="text-sm font-semibold mb-8" data-id="element-4052">
                      Pharmacist Signature:
                    </p>
                    <div className="border-b border-black" data-id="element-4053"></div>
                    <p className="text-sm mt-2 font-medium" data-id="element-4054">
                      {currentUser?.fullName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-4055">
                      BMED Pharmacy Dept
                    </p>
                  </div>
                  <div className="w-64" data-id="element-4056">
                    <p className="text-sm font-semibold mb-8" data-id="element-4057">
                      Official Stamp:
                    </p>
                    <div className="border border-black h-24 w-24 rounded-full mx-auto opacity-20" data-id="element-4058"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>;
    }
    // Standard Dispensing View
    return <div className="space-y-6 pb-20 print:hidden" data-id="element-4059">
        <div className="flex justify-between items-center sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-4060">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-4061">
            Dispense Medication
          </h1>
          <div className="flex space-x-2" data-id="element-4062">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-4063">
              Back to Queue
            </Button>
            {notDispensedDrugs.length > 0 && <Button variant="outline" onClick={() => setShowPrescriptionPreview(true)} className="border-blue-600 text-blue-600 hover:bg-blue-50" data-id="element-4064">
                <FileText className="w-4 h-4 mr-2" data-id="element-4065" /> Generate Prescription
              </Button>}
            <Button onClick={handleCompleteDispensing} disabled={!allProcessed} className={`${allProcessed ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-400 cursor-not-allowed'} text-white`} data-id="element-4066">
              <Check className="w-5 h-5 mr-2" data-id="element-4067" /> Complete Dispensing
            </Button>
          </div>
        </div>

        {/* Medical Form Style Container */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-4068">
          {/* Patient Header */}
          <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-4069">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-4070">
              <div className="col-span-8" data-id="element-4071">
                <span className="font-semibold" data-id="element-4072">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-4073">
                <span className="font-semibold" data-id="element-4074">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-4075">
                <span className="font-semibold" data-id="element-4076">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-4077">
              <div className="col-span-6" data-id="element-4078">
                <span className="font-semibold" data-id="element-4079">File No:</span>{' '}
                {patient.bmcFileNumber}
              </div>
              <div className="col-span-6" data-id="element-4080">
                <span className="font-semibold" data-id="element-4081">Prescribing Clinician:</span>{' '}
                {selectedEncounter.lastAttendingClinician}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-4082">
              <div className="col-span-12" data-id="element-4083">
                <span className="font-semibold" data-id="element-4084">Allergies:</span>{' '}
                <span className="text-red-600 font-bold" data-id="element-4085">
                  {patient.allergies}
                </span>
              </div>
            </div>
          </div>

          {/* DRUG TREATMENT / PRESCRIPTION */}
          <div className="border-2 border-black" data-id="element-4086">
            <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-4087">
              Drug Treatment / Prescription
            </h3>
            <table className="w-full border-collapse text-sm text-center" data-id="element-4088">
              <thead data-id="element-4089">
                <tr className="bg-gray-100" data-id="element-4090">
                  <th className="border border-black p-2 w-10" data-id="element-4091">No.</th>
                  <th className="border border-black p-2 text-left" data-id="element-4092">
                    Drug Name
                  </th>
                  <th className="border border-black p-2" data-id="element-4093">Dose</th>
                  <th className="border border-black p-2" data-id="element-4094">Route</th>
                  <th className="border border-black p-2" data-id="element-4095">Frequency</th>
                  <th className="border border-black p-2" data-id="element-4096">Days</th>
                  <th className="border border-black p-2 w-64" data-id="element-4097">
                    Dispense Status
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-4098">
                {selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-50' : dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-50' : ''} data-id="element-4099">
                    <td className="border border-black p-2 font-medium" data-id="element-4100">
                      {i + 1}
                    </td>
                    <td className="border border-black p-2 text-left font-bold" data-id="element-4101">
                      {rx.drugName}
                    </td>
                    <td className="border border-black p-2" data-id="element-4102">{rx.dose}</td>
                    <td className="border border-black p-2" data-id="element-4103">{rx.route}</td>
                    <td className="border border-black p-2" data-id="element-4104">{rx.frequency}</td>
                    <td className="border border-black p-2" data-id="element-4105">{rx.days}</td>
                    <td className="border border-black p-2" data-id="element-4106">
                      <div className="flex space-x-1 justify-center" data-id="element-4107">
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Dispensed')} data-id="element-4108">
                          <CheckCircle className="w-3 h-3 mr-1" data-id="element-4109" /> Dispensed
                        </button>
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Not Dispensed')} data-id="element-4110">
                          <XCircle className="w-3 h-3 mr-1" data-id="element-4111" /> Not Avail
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* NAME OF MEDICAL PRACTITIONER */}
          <div className="border-2 border-black p-4 mt-6" data-id="element-4112">
            <p className="font-bold text-sm" data-id="element-4113">
              Dispensing Pharmacist:{' '}
              <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-4114">
                {currentUser?.fullName || ''}
              </span>
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-4115">
      <div className="flex justify-between items-center" data-id="element-4116">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-4117">Pharmacy Dashboard</h1>
        <Badge variant="success" className="text-sm px-3 py-1 bg-teal-100 text-teal-800" data-id="element-4118">
          {pendingEncounters.length} Prescriptions Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-4119">
        <ul className="divide-y divide-gray-200" data-id="element-4120">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-4121">
                  <div className="flex items-center" data-id="element-4122">
                    <div className="bg-teal-100 p-2 rounded-full mr-4" data-id="element-4123">
                      <Pill className="h-6 w-6 text-teal-600" data-id="element-4124" />
                    </div>
                    <div data-id="element-4125">
                      <p className="text-sm font-medium text-blue-600" data-id="element-4126">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-4127">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-4128">
                        {encounter.prescriptions.length} items prescribed by{' '}
                        {encounter.lastAttendingClinician}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} className="bg-teal-600 hover:bg-teal-700 text-white" data-id="element-4129">
                    View Prescription
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-4130">
              <Pill className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-4131" />
              <p className="text-lg font-medium text-gray-900" data-id="element-4132">
                No pending prescriptions
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};